import type { Map as MapType } from "mapbox-gl";
import type React from "react";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Icon, Loader, Map } from "senf-atomic-design-system";
import type { Project } from "senf-shared/types";
import { convertColorToHSL, generateThemeColor } from "../../../styles/helpers";
import usePostcodes from "./hooks/usePostcodes";
import { flyToPolygon } from "./utils/flyToPolygon";

const PostcodeMap = ({
  project,
  data,
  statefulMap,
  setStatefulMap,
  resize,
}: {
  project: Project;
  data: any;
  statefulMap: MapType;
  setStatefulMap: React.Dispatch<React.SetStateAction<MapType>>;
  resize?: any;
}) => {
  const { t } = useTranslation();
  const [gisData, setGisData] = useState([{}]);

  const postcodes = data.reduce(
    (acc, res) => {
      const postcode =
        res.userData?.postcode || res.postcode || res.userPostcode;
      if (!postcode) return acc;
      return {
        ...acc,
        [postcode]: (acc[postcode] || 0) + 1,
      };
    },
    {} as Record<string, number>,
  );

  // Calculate min and max counts
  const counts = Object.values(postcodes);
  const maxCount = Math.max(...counts);
  const minCount = Math.min(...counts);

  // Improved function to normalize counts to opacity between 0.1 and 1
  const normalizeOpacity = (count) => {
    if (maxCount === minCount) return 1; // Avoid division by zero if all counts are the same
    // Ensure there's a distinct difference in opacity for values close to min or max
    const normalized = (count - minCount) / (maxCount - minCount);

    return 0.1 + normalized * 0.4; // Scale from 0.1 to 1.0
  };
  const { getPostalCodeBoundaries } = usePostcodes();

  useEffect(() => {
    const p = Object.keys(postcodes).map((e) => ({
      postalCode: e,
      countryCode: e.length === 5 ? "DE" : "AT",
    }));
    getPostalCodeBoundaries(p).then((results) => {
      // console.log(postcodes);
      const data = {
        type: "FeatureCollection",
        style: " ",
        features: results.features.map((e) => ({
          // ...e.geometry,
          geometry: e.geometry,
          properties: {
            color: generateThemeColor(convertColorToHSL(project.color)).color[
              "600"
            ],

            borderWidth: 0.4,
            // borderColor: "transparent",
            opacity: normalizeOpacity(postcodes[e.properties?.postal_code]),
            markerLabelData: postcodes[e.properties?.postal_code]
              ? [
                  { plz_name_long: e.properties?.note },
                  `${postcodes[e.properties?.postal_code]} ${t(
                    "global:participants",
                  )}`,
                ]
              : null,

            // tooltipLabel: `${e.name}: ${postcodes[e.name]}`,
          },
        })),
      };
      setGisData(data);
    });
  }, [data]);

  const isValidLatLng = (lat, lng) => {
    return lat >= -90 && lat <= 90 && lng >= -180 && lng <= 180;
  };

  useEffect(() => {
    if (gisData?.features) {
      const validFeatures = gisData.features.filter((feature) => {
        if (feature.geometry.type === "Point") {
          return isValidLatLng(
            feature.geometry.coordinates[1],
            feature.geometry.coordinates[0],
          );
        }
        if (feature.geometry.type === "Polygon") {
          return feature.geometry.coordinates[0].every((coord) =>
            isValidLatLng(coord[1], coord[0]),
          );
        }
        return true; // Für andere Geometrietypen
      });

      if (validFeatures.length !== gisData.features.length) {
        console.warn(
          "Einige Features wurden aufgrund ungültiger Koordinaten entfernt",
        );
      }

      const validGisData = { ...gisData, features: validFeatures };

      flyToPolygon(
        statefulMap,
        validGisData,
        1000,
        {
          top: 50,
          bottom: 50,
          left: 50,
          right: 50,
        },
        0,
      );
    }
  }, [gisData, data]);

  return (
    <div className="relative h-full w-full">
      {postcodes && gisData?.length <= 1 && (
        <Icon
          name="loading"
          className="absolute left-1/2 top-1/2 z-[9999999]  h-10 w-10 -translate-x-5 -translate-y-5"
        />
      )}
      <Map
        statefulMap={statefulMap}
        setStatefulMap={setStatefulMap}
        gisData={[gisData || {}]}
        optimizeString
        preserveDrawingBuffer={true}
        resize={resize}
      />
    </div>
  );
};

export default PostcodeMap;
